import { ReactNode, Suspense, createContext, useContext, useMemo } from 'react';

import { Logger, useNamedLogger } from '@eluve/logger';

import { useDb } from './FilesDbProvider';
import { resolveDeviceId } from './device-id';
import { DexieDb } from './files-db';

const DeviceIdContext = createContext<string | null>(null);

export const useDeviceId = (): string => {
  const value = useContext(DeviceIdContext);
  if (!value) {
    throw new Error('useDeviceId must be used within a DeviceIdProvider');
  }
  return value;
};

type DeviceIdGateProps = {
  db: DexieDb;
  userId: string;
  tenantId: string;
  logger: Logger;
  children: ReactNode;
};

const promiseCache = new Map<string, Promise<string>>();
const resolvedCache = new Map<string, string>();

const getOrCreatePromise = (args: {
  db: DexieDb;
  userId: string;
  tenantId: string;
  logger: Logger;
}): Promise<string> => {
  const key = `${args.userId}.${args.tenantId}`;
  const resolved = resolvedCache.get(key);
  if (resolved) {
    return Promise.resolve(resolved);
  }
  const existing = promiseCache.get(key);
  if (existing) {
    return existing;
  }
  // Drop the cached promise on rejection so a transient IDB/storage failure
  // doesn't leave Suspense throwing the same rejected promise on every retry.
  const promise = resolveDeviceId(args)
    .then((value) => {
      resolvedCache.set(key, value);
      return value;
    })
    .catch((error) => {
      promiseCache.delete(key);
      throw error;
    });
  promiseCache.set(key, promise);
  return promise;
};

const DeviceIdGate = ({
  db,
  userId,
  tenantId,
  logger,
  children,
}: DeviceIdGateProps) => {
  const key = `${userId}.${tenantId}`;
  const resolved = resolvedCache.get(key);
  if (!resolved) {
    throw getOrCreatePromise({ db, userId, tenantId, logger });
  }
  return (
    <DeviceIdContext.Provider value={resolved}>
      {children}
    </DeviceIdContext.Provider>
  );
};

type DeviceIdProviderProps = {
  userId: string;
  tenantId: string;
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Resolves the device ID once for the (userId, tenantId) pair via Suspense and
 * exposes it through React context. After mount, all reads come from context
 * with no further Dexie/localStorage I/O for the lifetime of the tab. [ENG-4180]
 */
export const DeviceIdProvider = ({
  userId,
  tenantId,
  fallback = null,
  children,
}: DeviceIdProviderProps) => {
  const db = useDb();
  const logger = useNamedLogger('DeviceIdProvider');
  const memoizedChildren = useMemo(() => children, [children]);

  return (
    <Suspense fallback={fallback}>
      <DeviceIdGate db={db} userId={userId} tenantId={tenantId} logger={logger}>
        {memoizedChildren}
      </DeviceIdGate>
    </Suspense>
  );
};

export const __resetDeviceIdProviderCacheForTests = (): void => {
  promiseCache.clear();
  resolvedCache.clear();
};
